import axios from '@/lib/axios';
import { bookingQueryKeys } from '@/lib/booking-query-keys';
import { useQuery, type UseQueryOptions } from '@tanstack/react-query';

export interface Cleaner {
  booked_cleaner_id: string;
  cleaner_id: string;
  no_of_hours: number;
  no_of_cleaners: number;
  assigned_date: string;
  cleaner_firstname: string;
  cleaner_lastname: string;
  cleaner_email: string;
  cleaner_phone: string;
  cleaner_profile_pic: string | null;
  has_review: boolean;
  rating: number | null;
  comments: string | null;
  /** Tip amount in dollars when present (customer booking payloads). */
  tip_amount?: number | null;
  /** Actual hours worked when job finished (customer list/detail). */
  real_no_of_hours?: number | null;
}

export interface Booking {
  booking_id: string;
  booking_date: string;
  booking_time: string;
  booking_date_time?: string | null;
  type_of_service: string;
  status:
    | 'pending'
    | 'accepted'
    | 'confirmed'
    | 'completed'
    | 'canceled'
    | 'refused'
    | 'upcoming'
    | 'ongoing'
    | 'requested';
  address: string;
  /** Apt/suite/unit from the booking address, present when detail is merged in. */
  apt_suite_unit?: string;
  city: string;
  state: string;
  zipcode?: string;
  country: string;
  customer_id?: string;
  customer_firstname?: string;
  customer_lastname?: string;
  customer_email?: string;
  customer_phone?: string;
  payment_id?: string;
  total_amount: number;
  /** Customer list payloads: amount actually charged (matches mobile history). */
  total_amount_charged?: number | string | null;
  /** Actual hours (job); use for completed duration like customer app. */
  real_no_of_hours?: number | null;
  /** Booked duration in hours (placeholder row when no cleaner assigned yet). */
  booked_hours?: number | null;
  no_of_hours?: number | null;
  duration_hours?: number | null;
  payment_date: string;
  transaction_id?: string;
  cleaners: Cleaner[];
}

export interface BookingResponse {
  bookings: Booking[];
}

export interface BookingQueryParams {
  sort_by?: 'created_date' | 'booking_date' | 'booking_time' | 'status' | 'id';
  sort_order?: 'asc' | 'desc';
  status?:
    | 'pending'
    | 'accepted'
    | 'confirmed'
    | 'completed'
    | 'canceled'
    | 'upcoming'
    | 'ongoing'
    | 'all';
  history?: 'upcoming' | 'history' | 'all';
}

const getBookingQuery = async (
  userid: string,
  params?: BookingQueryParams
): Promise<BookingResponse> => {
  const queryParams = new URLSearchParams();

  if (params?.sort_by) {
    queryParams.append('sort_by', params.sort_by);
  }
  if (params?.sort_order) {
    queryParams.append('sort_order', params.sort_order);
  }
  if (params?.status) {
    queryParams.append('status', params.status);
  }
  if (params?.history) {
    queryParams.append('history', params.history);
  }

  const queryString = queryParams.toString();
  const url = `/booking/get/${userid}/${queryString ? `?${queryString}` : ''}`;

  const response = await axios.get(url);
  return response.data;
};

export const useGetBookingQuery = (
  userid: string,
  params?: BookingQueryParams,
  options?: Partial<UseQueryOptions<BookingResponse>>
) => {
  const status = params?.status?.toLowerCase();
  const isActiveList =
    status === 'ongoing' ||
    status === 'upcoming' ||
    params?.history === 'upcoming';

  return useQuery({
    queryKey: bookingQueryKeys.list(userid, params as Record<string, unknown>),
    queryFn: () => getBookingQuery(userid, params),
    retry: false,
    enabled: !!userid && userid.trim().length > 0,
    refetchInterval: isActiveList ? 15 * 1000 : false,
    ...options,
  });
};
